import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users, Plus, Search, Phone, Mail, MapPin, Trash2, Save, X } from 'lucide-react';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  total_spent: number;
  last_contact: string;
  created_at: string;
}

export function CustomerCRM() {
  const navigate = useNavigate();
  const { config } = useBusinessConfig();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });

  const isStarter = config?.plan === 'starter';

  // Get authenticated user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setUserId(user.id);
    };
    getUser();
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!userId) {
      toast.error('You must be logged in to add customers');
      return;
    }

    // Check Starter plan limit (30 customers)
    if (isStarter) {
      const { count, error: countError } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (countError) {
        console.error('Failed to count customers:', countError);
      } else if (count !== null && count >= 30) {
        toast.error('Starter plan limit reached (30 customers). Upgrade to Pro for unlimited customers.');
        navigate('/upgrade');
        return;
      }
    }

    try {
      const { error } = await supabase
        .from('customers')
        .insert([{
          ...newCustomer,
          user_id: userId,
          total_spent: 0,
          last_contact: new Date().toISOString(),
        }]);

      if (error) throw error;
      toast.success('Customer added successfully!');
      setNewCustomer({ name: '', email: '', phone: '', address: '', notes: '' });
      setIsAdding(false);
      fetchCustomers();
    } catch (error) {
      console.error('Add customer error:', error);
      toast.error('Failed to add customer');
    }
  };

  const handleDeleteCustomer = async (id: string) => {
    if (!confirm('Are you sure you want to delete this customer?')) return;

    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Customer deleted');
      fetchCustomers();
    } catch {
      toast.error('Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm)
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-black uppercase">Customer CRM</h2>
        </div>
        <Button
          onClick={() => setIsAdding(!isAdding)}
          className="flex items-center gap-2"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          Add Customer
        </Button>
      </div>

      {/* Add Customer Form */}
      {isAdding && (
        <div className="bg-white p-6 rounded-2xl border-2 border-slate-100 space-y-4">
          <h3 className="font-black uppercase text-slate-900">New Customer</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Name *</label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                className="h-12 rounded-xl"
                placeholder="Customer name"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Email *</label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                className="h-12 rounded-xl"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Phone</label>
              <Input
                type="tel"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                className="h-12 rounded-xl"
                placeholder="0400 000 000"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Address</label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                className="h-12 rounded-xl"
                placeholder="123 Street, Suburb"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Notes</label>
            <textarea
              value={newCustomer.notes}
              onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
              className="w-full h-24 rounded-xl border border-slate-200 p-4 font-bold resize-none"
              placeholder="Additional notes..."
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddCustomer} className="flex-1">
              <Save className="w-4 h-4 mr-2" /> Save Customer
            </Button>
            <Button variant="outline" onClick={() => setIsAdding(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          type="text"
          placeholder="Search customers by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-12 rounded-xl border-slate-200 font-bold"
        />
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading customers...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-bold text-sm">No customers found</p>
          <p className="text-xs mt-1">Add your first customer to get started</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              className="bg-white border-2 border-slate-100 rounded-2xl p-6 hover:border-purple-200 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-black text-slate-900 uppercase">{customer.name}</h3>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {customer.email && (
                      <a href={`mailto:${customer.email}`} className="flex items-center gap-1 text-sm text-purple-600 hover:underline">
                        <Mail className="w-4 h-4" />
                        {customer.email}
                      </a>
                    )}
                    {customer.phone && (
                      <a href={`tel:${customer.phone}`} className="flex items-center gap-1 text-sm text-purple-600 hover:underline">
                        <Phone className="w-4 h-4" />
                        {customer.phone}
                      </a>
                    )}
                    {customer.address && (
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        {customer.address}
                      </div>
                    )}
                  </div>
                  {customer.notes && (
                    <p className="text-sm text-slate-600 mt-3">{customer.notes}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className="font-bold">
                      Total Spent: ${customer.total_spent?.toLocaleString() || '0'}
                    </span>
                    <span>
                      Last Contact: {new Date(customer.last_contact).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCustomer(customer.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
